// Turnstile.tsx
//
// The captcha widget, and the token it produces for Supabase Auth
// (takenote_decisions.md 7.2).
//
// ---------------------------------------------------------------------------
// THE TOKEN GOES TO GOTRUE, NOT TO US. There is no /api/captcha/verify in this
// system and there is not going to be one: GoTrue verifies the token against
// Cloudflare server-side using the secret in `[auth.captcha]`, and refuses the
// request itself if it does not check out. Nothing this project wrote ever
// holds a captcha secret, calls siteverify, or stores a "captcha ok" flag.
//
// WHICH REQUESTS NEED ONE. Captcha in GoTrue is a global setting, not a
// per-route one, so every page that reaches an auth entry point needs a widget.
// Measured against the running service (v2.191.0) rather than assumed:
//
//   POST /signup                        REQUIRED   SignupPage
//   POST /token?grant_type=password     REQUIRED   LoginPage, DeleteAccountPage
//   POST /resend                        REQUIRED   VerifyEmailPage
//   POST /recover, /otp                 REQUIRED   ForgotPasswordPage
//   POST /token?grant_type=refresh_token  not required
//   POST /admin/users                     not required
//
// The last two lines are why the extension keeps working: its session refreshes
// need no captcha, and `pnpm e2e` creates accounts through the admin API.
// ---------------------------------------------------------------------------
import { useEffect, useImperativeHandle, useRef, forwardRef, useState } from "react";

const SCRIPT_SRC = "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";
const SCRIPT_ID = "cf-turnstile-script";

/** The sitekey, from the environment. See apps/web/.env.example for the test one. */
export const TURNSTILE_SITEKEY = import.meta.env.VITE_TURNSTILE_SITEKEY as string | undefined;

/** Is a captcha configured at all? False in a checkout that has not set the key. */
export function turnstileConfigured(): boolean {
  return typeof TURNSTILE_SITEKEY === "string" && TURNSTILE_SITEKEY.length > 0;
}

/**
 * What the five pages used to say all the time, and now say only when it is true.
 *
 * ---------------------------------------------------------------------------
 * WHY THIS SENTENCE IS THE COMPONENT'S AND NOT EACH PAGE'S (Phase 11.3).
 *
 * Five pages carried a standing hint - "Complete the human check to continue"
 * - under a disabled button. It was noise on every ordinary visit, because the
 * widget above it already says what it wants. It earned its place for exactly
 * one case: when the check cannot be SHOWN at all - offline, a network filter,
 * an ad blocker - the button is disabled for ever and nothing on the page says
 * why. 11.3 removes the hint and keeps that one case, which means something has
 * to tell the two apart. Nothing could: a failure and "not solved yet" both
 * arrived at the page as `onToken(null)`.
 *
 * The failure is tracked HERE and rendered HERE, rather than handed to five
 * pages as a second callback. Only this component knows it; every page shows
 * the identical sentence; and a page holding a `captchaFailed` boolean it does
 * nothing with but print one fixed string has gained a way to drift and nothing
 * else. What the pages keep is the submit rule - no token, no submit - which is
 * unchanged, because a failure still clears the token.
 *
 * It does NOT take layout away from the page. The component still occupies one
 * block in the form's flow; the sentence sits inside that block, under the
 * widget it is about. The page decides where the block goes and what surrounds
 * it, exactly as before.
 * ---------------------------------------------------------------------------
 */
export const TURNSTILE_FAILED =
  "The human check didn't load. Check your connection and reload the page.";

/**
 * The OTHER failure: Cloudflare ran the check and could not clear this visitor
 * (its `error-callback`, e.g. code 600010 "generic challenge failure"). Saying
 * "didn't load, check your connection" here is false - nothing failed to load, and
 * reloading just repeats the answer. Cloudflare documents these as retriable, so
 * the page says what happened and offers a retry in one click.
 */
export const TURNSTILE_REFUSED = "We couldn't confirm you're human.";
export const TURNSTILE_RETRY = "Try again";
/** After this many refusals in a row, a person with nowhere else to go is told who to write to. */
export const REFUSALS_BEFORE_SUPPORT = 2;
export const TURNSTILE_SUPPORT = "If this keeps happening, email support@takenote.fyi.";

type TurnstileApi = {
  render: (el: HTMLElement, opts: Record<string, unknown>) => string;
  reset: (id?: string) => void;
  remove: (id?: string) => void;
};

declare global {
  interface Window {
    turnstile?: TurnstileApi;
  }
}

/**
 * Load Cloudflare's script once per page, however many widgets ask for it.
 *
 * The promise is cached at module scope rather than per component: React 18's
 * StrictMode mounts every effect twice in development, and without this the
 * signup page would append two script tags and race them.
 */
let scriptPromise: Promise<void> | null = null;

function loadScript(): Promise<void> {
  if (scriptPromise) return scriptPromise;

  scriptPromise = new Promise<void>((resolve, reject) => {
    if (window.turnstile) return resolve();

    const existing = document.getElementById(SCRIPT_ID) as HTMLScriptElement | null;
    if (existing) {
      existing.addEventListener("load", () => resolve());
      existing.addEventListener("error", () => reject(new Error("Turnstile script failed to load")));
      return;
    }

    const s = document.createElement("script");
    s.id = SCRIPT_ID;
    s.src = SCRIPT_SRC;
    s.async = true;
    s.defer = true;
    s.onload = () => resolve();
    s.onerror = () => reject(new Error("Turnstile script failed to load"));
    document.head.appendChild(s);
  });

  return scriptPromise;
}

export type TurnstileHandle = {
  /**
   * Throw the current token away and challenge again.
   *
   * A Turnstile token is SINGLE USE and lasts 300 seconds. Any auth request
   * that consumed one - including one that then failed for an unrelated reason,
   * such as a wrong password - has spent it, and retrying with the same token
   * is refused by Cloudflare as `timeout-or-duplicate`. So every form here
   * resets the widget after a failed submit; without it the second attempt
   * fails for a reason that has nothing to do with what the user typed.
   */
  reset: () => void;
};

type Props = {
  /** Called with a fresh token, and with null when it expires or errors. */
  onToken: (token: string | null) => void;
  /**
   * Widget theme. "auto" follows the browser's light or dark setting, as the
   * pages do since Phase 12.5 - a fixed "light" would be a white box on a dark
   * page.
   */
  theme?: "light" | "dark" | "auto";
};

/**
 * The widget.
 *
 * 340 px AND CENTRED, decided here rather than on each page. Cloudflare's
 * default is a fixed 300 px box, which 11.3 centred - hard against the left edge
 * of a 400 px form it read as half-drawn. Phase 12.5 tried the full 400 px
 * ("flexible", which fills its container with a 300 px minimum) and it looked
 * stretched; 340 is wider than the default and still visibly short of the
 * fields. Five copies of one layout decision is five chances to get one of
 * them wrong, so the five pages say where the block goes and this says what it
 * looks like.
 *
 * Renders its own notice when no sitekey is configured, and says so - a silent
 * empty box on a signup form that cannot be submitted is the kind of dead end
 * 9.5 had to fix twice. That notice is why no page needs a line about an
 * unconfigured build: it is already on the screen, in amber, saying more.
 */
const Turnstile = forwardRef<TurnstileHandle, Props>(function Turnstile(
  { onToken, theme = "auto" },
  ref
) {
  const hostRef = useRef<HTMLDivElement | null>(null);
  const widgetIdRef = useRef<string | null>(null);

  /**
   * "The check could not be presented", which is NOT "you have not solved it".
   *
   * THREE THINGS ARRIVE AS `onToken(null)` AND ONLY TWO OF THEM ARE THIS:
   *
   *   the script never loaded   FAILURE. There is no widget on the page at all,
   *                             so nothing else will ever say why the button is
   *                             dead. This is the case the sentence exists for,
   *                             and the one the by-hand check reproduces by
   *                             blocking challenges.cloudflare.com.
   *
   *   `error-callback`          FAILURE, but a different one: the widget rendered
   *                             and Cloudflare could not clear this visitor. The
   *                             widget is hidden (interaction-only), so nothing
   *                             else explains the dead button. It says so, in
   *                             words that are true, and offers "Try again"
   *                             (Cloudflare calls these errors retriable).
   *
   *   `expired-callback`        NOT a failure. The user solved it and the token
   *                             aged out after 300 s. The widget says so itself
   *                             and offers a retry in place, so "didn't load"
   *                             would be the page contradicting what the user
   *                             can see.
   *
   * Solving it clears the flag, so a widget that retried itself after a blip
   * does not leave a stale sentence behind.
   */
  const [problem, setProblem] = useState<"load" | "refused" | null>(null);
  // Refusals in a row. Only a solved check clears it, so "Try again" followed by
  // another refusal counts as two, and the second shows where to write.
  const [refusals, setRefusals] = useState(0);

  // Held in a ref so the effect below can stay mounted for the life of the
  // page. Passing `onToken` as a dependency would re-render the widget on every
  // parent state change, and re-rendering a Turnstile widget re-challenges the
  // user.
  const onTokenRef = useRef(onToken);
  onTokenRef.current = onToken;

  // Shared by a form's own reset (its token was spent) and the "Try again" button.
  // Only does anything where a widget exists and is about to be challenged again,
  // so whatever failed before is no longer what the user is looking at.
  function retry() {
    if (window.turnstile && widgetIdRef.current) {
      window.turnstile.reset(widgetIdRef.current);
      onTokenRef.current(null);
      setProblem(null);
    }
  }

  useImperativeHandle(ref, () => ({ reset: retry }), []);

  useEffect(() => {
    if (!turnstileConfigured()) return;

    let cancelled = false;

    void loadScript()
      .then(() => {
        if (cancelled || !hostRef.current || !window.turnstile) return;

        // StrictMode's second mount runs with the same DOM node. Rendering
        // twice into it would leave two widgets stacked on the page.
        if (widgetIdRef.current) return;

        widgetIdRef.current = window.turnstile.render(hostRef.current, {
          sitekey: TURNSTILE_SITEKEY,
          theme,
          size: "flexible",
          // SHOWN ONLY WHEN CLOUDFLARE WANTS A PERSON TO ACT (Phase 14.5). The
          // check still runs on every page and a token still arrives; most
          // visitors never see the box. Not the widget's "Invisible" mode, which
          // never shows, so a visitor Cloudflare doubts would be refused with
          // nothing to click. The widget stays Managed in the Cloudflare
          // dashboard - this is how it is drawn, not how it decides.
          appearance: "interaction-only",
          callback: (token: string) => {
            setProblem(null);
            setRefusals(0);
            onTokenRef.current(token);
          },
          "expired-callback": () => onTokenRef.current(null),
          "error-callback": () => {
            setProblem("refused");
            setRefusals((n) => n + 1);
            onTokenRef.current(null);
          },
        });
      })
      .catch(() => {
        // Offline, blocked, or Cloudflare is down. Nothing rendered, so without
        // the sentence below the form is dead and silent - which is exactly
        // what the standing hints used to cover for.
        if (!cancelled) {
          setProblem("load");
          onTokenRef.current(null);
        }
      });

    return () => {
      cancelled = true;
      if (window.turnstile && widgetIdRef.current) {
        try {
          window.turnstile.remove(widgetIdRef.current);
        } catch {
          // Already gone; nothing to undo.
        }
        widgetIdRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!turnstileConfigured()) {
    return (
      <div className="flex justify-center">
        <div className="rounded-xl border border-warning-line bg-warning-bg p-3 text-sm text-warning">
          This build has no <span className="font-mono">VITE_TURNSTILE_SITEKEY</span>, so the
          human check cannot be shown and the form cannot be submitted. See{" "}
          <span className="font-mono">apps/web/.env.example</span>.
        </div>
      </div>
    );
  }

  return (
    <div>
      {/* 340px, centred. On a form narrower than Cloudflare's 300px minimum (a
          360px phone leaves 280px) it stays 300 and the flex centring spreads
          the overhang, 10px each side, rather than 20px on the right.
          No reserved height (Phase 14.5): the widget is usually hidden
          (interaction-only), and 65px kept for it would be an empty gap in
          every form. When Cloudflare does show it, the form grows to fit. */}
      <div className="flex justify-center">
        <div ref={hostRef} className="w-full max-w-[340px] min-w-[300px] shrink-0" />
      </div>
      {problem === "load" && <div className="mt-1 text-center text-xs text-danger">{TURNSTILE_FAILED}</div>}
      {problem === "refused" && (
        <div className="mt-1 text-center text-xs text-danger">
          {TURNSTILE_REFUSED}{" "}
          {/* type="button": this sits inside the page's form, and a retry must not submit it. */}
          <button type="button" onClick={retry} className="underline text-link-strong">
            {TURNSTILE_RETRY}
          </button>
          {refusals >= REFUSALS_BEFORE_SUPPORT && <div className="mt-1">{TURNSTILE_SUPPORT}</div>}
        </div>
      )}
    </div>
  );
});

export default Turnstile;
